class AuaArticle {
  constructor(root) {
    this.root = root;
    this.body = root.querySelector('[data-aua-article-body]');
    this.progress = root.querySelector('[data-aua-progress]');
    this.toc = root.querySelector('[data-aua-toc-links]');
    this.headings = [];
    this.initProgress();
    this.initToc();
    this.initCopyButtons();
    this.initBuyBoxes();
  }

  initProgress() {
    if (!this.progress || !this.body) return;
    const update = () => {
      const rect = this.body.getBoundingClientRect();
      const start = window.scrollY + rect.top - 120;
      const end = start + this.body.offsetHeight - window.innerHeight;
      const value = end > start ? ((window.scrollY - start) / (end - start)) * 100 : 0;
      this.progress.style.width = `${Math.min(100, Math.max(0, value))}%`;
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  initToc() {
    if (!this.toc || !this.body) return;
    this.headings = Array.from(this.body.querySelectorAll('h2, h3'));
    if (!this.headings.length) {
      const card = this.toc.closest('.aua-side-card');
      if (card) card.hidden = true;
      return;
    }

    const used = new Set();
    this.headings.forEach((heading, index) => {
      let base = heading.id || heading.textContent.trim().toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      if (!base) base = `section-${index + 1}`;
      let id = base;
      let suffix = 2;
      while (used.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
        id = `${base}-${suffix++}`;
      }
      used.add(id);
      heading.id = id;

      const link = document.createElement('a');
      link.href = `#${id}`;
      link.className = `aua-toc__link${heading.tagName === 'H3' ? ' is-sub' : ''}`;
      link.textContent = heading.textContent.trim();
      this.toc.appendChild(link);
    });

    if ('IntersectionObserver' in window) {
      const links = Array.from(this.toc.querySelectorAll('a'));
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((link) => link.classList.toggle('is-active', link.hash === `#${entry.target.id}`));
        });
      }, { rootMargin: '-20% 0px -68% 0px', threshold: 0 });
      this.headings.forEach((heading) => observer.observe(heading));
    }
  }

  initCopyButtons() {
    this.root.querySelectorAll('[data-copy-url]').forEach((button) => {
      button.addEventListener('click', async () => {
        const original = button.textContent;
        try {
          await navigator.clipboard.writeText(button.dataset.copyUrl);
          button.textContent = 'Copied';
        } catch (error) {
          const input = document.createElement('textarea');
          input.value = button.dataset.copyUrl;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
          button.textContent = 'Copied';
        }
        window.setTimeout(() => { button.textContent = original; }, 1600);
      });
    });
  }

  money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 })
      .format(Number(cents || 0) / 100);
  }

  initBuyBoxes() {
    this.root.querySelectorAll('[data-aua-buybox]').forEach((box) => {
      let product;
      try { product = JSON.parse(box.dataset.product); } catch (error) { return; }
      const form = box.querySelector('form');
      const idInput = box.querySelector('[name="id"]');
      const selectors = Array.from(box.querySelectorAll('[data-option-index]'));
      const button = box.querySelector('[data-buy-button]');
      const currentPrice = box.querySelector('[data-current-price]');
      const comparePrice = box.querySelector('[data-compare-price]');
      const saving = box.querySelector('[data-saving]');

      const update = () => {
        const options = selectors.map((select) => select.value);
        const variant = selectors.length
          ? product.variants.find((item) => item.options.every((value, index) => value === options[index]))
          : product.variants.find((item) => String(item.id) === String(idInput.value)) || product.variants[0];
        if (!variant) return;

        idInput.value = variant.id;
        if (currentPrice) currentPrice.textContent = this.money(variant.price);
        const hasDiscount = Number(variant.compare_at_price) > Number(variant.price);
        if (comparePrice) {
          comparePrice.hidden = !hasDiscount;
          comparePrice.textContent = hasDiscount ? this.money(variant.compare_at_price) : '';
        }
        if (saving) {
          saving.hidden = !hasDiscount;
          saving.textContent = hasDiscount ? `Save ${this.money(variant.compare_at_price - variant.price)}` : '';
        }
        if (button) {
          button.disabled = !variant.available;
          button.textContent = variant.available ? button.dataset.availableLabel : button.dataset.soldoutLabel;
        }
      };

      selectors.forEach((select) => select.addEventListener('change', update));
      update();

      if (!form) return;
      form.addEventListener('submit', async (event) => {
        if (!window.fetch || button.disabled) return;
        event.preventDefault();
        button.disabled = true;
        const label = button.textContent;
        button.textContent = button.dataset.loadingLabel;
        try {
          const rootPath = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Unable to add item');
          window.location.href = `${rootPath}checkout`;
        } catch (error) {
          button.disabled = false;
          button.textContent = label;
          form.submit();
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-aua-article]').forEach((root) => new AuaArticle(root));
});

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-aua-article]').forEach((root) => new AuaArticle(root));
});
